import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import directoryService from './directoryService';

import type { PaginatedRequestParams, PaginatedResponse, UserFilterParams } from '@/types/api';
import type { Company } from '@/types/directory/Company';
import type { User, UserPermissions } from '@/types/directory/User';

import { DirectoryQueryKey } from '@/constants/QueryKeys';

// Must stay within the backend's PaginationSettings.MaximumPageSize (50). A larger
// value makes the paginated collector's first request to GET /Directory/user (or
// /Directory/user/me/employee) fail with HTTP 400 (PaginatedRequestValidator:
// PageSize InclusiveBetween(1, 50)). That 400 surfaced as the generic "Une erreur
// est survenue" toast when clicking Next in the rule-based assignment method.
const COLLECT_PAGE_SIZE = 50;
// Hard cap to keep the paginated collector bounded even if the backend reports
// `hasNextPage: true` indefinitely. 200 pages × 100 per page = 20k employees,
// which comfortably covers a very large company.
const COLLECT_MAX_PAGES = 200;

const useGetCompanyInformation = (options?: { enabled?: boolean }) => {
    return useQuery<Company>({
        queryKey: [DirectoryQueryKey.COMPANY_INFORMATION],
        queryFn: directoryService.getCompanyInformation,
        enabled: options?.enabled,
    });
};

/**
 * Fetches company people based on user permissions.
 * - If canViewResultsOfAllDepartments is true: fetches all company users
 * - If canViewResultsOfAllDepartments is false: fetches only employees under the manager
 * The query remains disabled until permissions are resolved.
 */
const useGetCompanyPeople = (
    params?: PaginatedRequestParams,
    options?: {
        enabled?: boolean;
        canViewResultsOfAllDepartments?: boolean;
    },
) => {
    const canViewAll = options?.canViewResultsOfAllDepartments;
    // Enable query if explicitly enabled and permissions are resolved (not undefined)
    // or if permissions are explicitly false (meaning user can only see employees)
    const isEnabled = (options?.enabled ?? true) && canViewAll !== undefined;

    return useQuery<PaginatedResponse<User>>({
        queryKey: [
            canViewAll ? DirectoryQueryKey.COMPANY_USERS : DirectoryQueryKey.COMPANY_EMPLOYEES,
            params,
        ],
        queryFn: canViewAll
            ? () => directoryService.getCompanyUsers(params)
            : () => directoryService.getCompanyEmployees(params),
        enabled: isEnabled,
        placeholderData: keepPreviousData,
    });
};

const useGetUsersByIds = ({ userIds, identifier }: { userIds: string[]; identifier: string }) => {
    return useQuery<User[]>({
        queryKey: [DirectoryQueryKey.USERS_BY_IDS, identifier, userIds],
        queryFn: () => directoryService.getUsersByIds(userIds),
        enabled: userIds.length > 0,
    });
};

const useGetUserById = ({ userId }: { userId: string }) => {
    return useQuery<User>({
        queryKey: [DirectoryQueryKey.USER_BY_ID, userId],
        queryFn: () => directoryService.getUserById(userId),
        enabled: !!userId,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

const useGetCompanyUsers = (params?: PaginatedRequestParams, options?: { enabled?: boolean }) => {
    return useQuery<PaginatedResponse<User>>({
        queryKey: [DirectoryQueryKey.COMPANY_USERS, params],
        queryFn: () => directoryService.getCompanyUsers(params),
        enabled: options?.enabled,
        placeholderData: keepPreviousData,
    });
};

const useGetAllCompanyUsers = (options?: { enabled?: boolean; isActiveOnly?: boolean }) => {
    return useQuery<User[]>({
        queryKey: [DirectoryQueryKey.COMPANY_USERS, 'all', options?.isActiveOnly],
        queryFn: ({ signal }) =>
            directoryService.getAllCompanyUsers({
                isActiveOnly: options?.isActiveOnly,
                signal,
            }),
        enabled: options?.enabled,
    });
};

/**
 * Fetches the full employee list scoped by the user's performance scope permission.
 * - canManageAllDepartmentsPerformance: fetches every company user
 * - canManageTeamPerformance only: fetches the manager's own team (direct + indirect reports)
 *   using a recursive hierarchy walk to include all levels
 * - neither: query stays disabled (access is denied upstream at the route level)
 *
 * @param options.includeSelf - If true, includes the current user in the result.
 *   Use this for Performance Profile (user can view their own profile).
 *   Do NOT use for EmployeeSelector (manager cannot assign assessment to themselves).
 */
const useGetAllCompanyOrTeamUsers = (options?: {
    enabled?: boolean;
    canManageAllDepartmentsPerformance?: boolean;
    canManageTeamPerformance?: boolean;
    includeSelf?: boolean;
}) => {
    const canManageAllDepartments = options?.canManageAllDepartmentsPerformance;
    const canManageTeam = options?.canManageTeamPerformance;
    const includeSelf = options?.includeSelf ?? false;
    const isEnabled = (options?.enabled ?? true) && (canManageAllDepartments || canManageTeam);

    return useQuery<User[]>({
        queryKey: [
            DirectoryQueryKey.COMPANY_USERS,
            'scoped',
            canManageAllDepartments,
            canManageTeam,
            includeSelf,
        ],
        queryFn: ({ signal }) =>
            canManageAllDepartments
                ? directoryService.getAllCompanyUsers({
                      isActiveOnly: true,
                      includeInvited: true,
                      signal,
                  })
                : directoryService.getAllHierarchyEmployees({ includeSelf, signal }),
        enabled: isEnabled,
    });
};

/**
 * The reader's direct reports, as the directory's reporting line names them (the employees whose
 * manager the reader is), whatever the reader's permissions: the one answer to "does the reader
 * have a team of their own".
 */
const useGetDirectReports = () => {
    return useQuery<User[]>({
        queryKey: [DirectoryQueryKey.DIRECT_REPORTS],
        queryFn: ({ signal }) => directoryService.getAllManagerEmployees({ signal }),
    });
};

const useGetUserPermissions = () => {
    return useQuery<UserPermissions>({
        queryKey: [DirectoryQueryKey.USER_PERMISSIONS],
        queryFn: () => directoryService.getUserPermissions(),
    });
};

/**
 * Returns an imperative resolver that walks every page of the permission-scoped
 * company-people endpoint and collects the FULL list of matched employee ids for
 * the given filter params. Used when confirming an assignment by rule, where the
 * reactive (single-page) `useGetCompanyPeople` is only enough for the preview count.
 */
const useCollectCompanyPeopleIds = (options?: { canViewResultsOfAllDepartments?: boolean }) => {
    const canViewAll = options?.canViewResultsOfAllDepartments;

    return useCallback(
        async (filterParams: UserFilterParams): Promise<string[]> => {
            const fetchPage = (pageNumber: number) => {
                const params: PaginatedRequestParams = {
                    pageNumber,
                    pageSize: COLLECT_PAGE_SIZE,
                    isActiveOnly: true,
                    includeInvited: true,
                    ...filterParams,
                };

                return canViewAll
                    ? directoryService.getCompanyUsers(params)
                    : directoryService.getCompanyEmployees(params);
            };

            const ids: string[] = [];
            let pageNumber = 1;
            let hasNextPage = true;

            while (hasNextPage) {
                const page = await fetchPage(pageNumber);
                const items = page.items ?? [];

                // Stop on an empty page: a backend that keeps returning
                // `hasNextPage: true` with no items would otherwise loop forever.
                if (items.length === 0) {
                    break;
                }

                items.forEach((user) => ids.push(user.id));
                hasNextPage = page.hasNextPage;
                pageNumber += 1;

                if (pageNumber > COLLECT_MAX_PAGES) {
                    console.warn(
                        `useCollectCompanyPeopleIds: stopped after ${COLLECT_MAX_PAGES} pages (collected ${ids.length} ids). The result may be truncated.`,
                    );
                    break;
                }
            }

            return ids;
        },
        [canViewAll],
    );
};

export {
    useGetCompanyInformation,
    useGetCompanyPeople,
    useGetUsersByIds,
    useGetUserById,
    useGetCompanyUsers,
    useGetAllCompanyUsers,
    useGetAllCompanyOrTeamUsers,
    useGetDirectReports,
    useGetUserPermissions,
    useCollectCompanyPeopleIds,
};
